/**
 * The SID-style voice engine (05 §1): turns a `VoiceSpec` into WebAudio nodes on the audio clock.
 *
 *   triangle | sawtooth | pulse | held noise --> [resonant filter] --> ADSR gain --> [pan] --> bus
 *
 * Triangle and sawtooth are WebAudio's own. A fixed-width pulse is a PeriodicWave built from
 * the width. A pulse with PWM is a sawtooth minus itself delayed by width / frequency, the delay
 * swung by an LFO - the classic analogue trick, and the SID lead's moving, chorusy sound. Noise is
 * a looped buffer of held random values whose playback rate is the noise "pitch", so a falling
 * pitch on noise sounds like the chip's. Voices are built per note and disconnect when done.
 */

import { pulseCoefficients } from './notes';
import { FRAME, type FilterSpec, type VoiceSpec } from './voice';

const PULSE_HARMONICS = 96;
/** Widths are rounded to this many steps so the PeriodicWave cache stays small. */
const WIDTH_STEPS = 64;
/** A cap on scheduled pitch steps per voice (a 30 s arpeggio at 50 Hz). */
const MAX_STEPS = 1500;

/** A voice once started: when it sounds, and how to silence it early (voice stealing). */
export interface Playing {
  readonly start: number;
  readonly end: number;
  cut(at: number): void;
}

interface Source {
  readonly out: AudioNode;
  readonly pitch: AudioParam;
  readonly detune: AudioParam;
  /** Hz -> the pitch param's units (1 for oscillators; 1 / noiseHz for the noise's playback rate). */
  readonly scale: number;
}

/** Everything a voice made, to start, stop and disconnect together. */
interface Parts {
  readonly nodes: AudioNode[];
  readonly sources: AudioScheduledSourceNode[];
}

export class Sid {
  private readonly pulses = new Map<number, PeriodicWave>();
  private noiseOffset = 0;

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly noise: AudioBuffer,
    private readonly noiseHz: number,
  ) {}

  /** Start `v` at audio time `t0` (plus its own `at`) into `out`, scaled by `gain`, panned -1..1. */
  play(v: VoiceSpec, t0: number, out: AudioNode, gain = 1, pan = 0): Playing {
    const ctx = this.ctx;
    const t = t0 + v.at;
    const gate = Math.max(0.001, v.gate);
    const end = t + gate + v.env.r;
    const parts: Parts = { nodes: [], sources: [] };
    const src = this.source(v, t, parts);
    this.pitch(src, v, t, gate, end);
    if (v.vibrato) this.vibrato(src, v.vibrato, t, parts);
    let head = src.out;
    if (v.filter) head = this.filter(head, v.filter, t, end - t, parts);
    const amp = ctx.createGain();
    parts.nodes.push(amp);
    this.envelope(amp.gain, v, t, gate, v.level * gain);
    head = head.connect(amp);
    if (pan !== 0 && typeof ctx.createStereoPanner === 'function') {
      const p = ctx.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pan));
      parts.nodes.push(p);
      head = head.connect(p);
    }
    head.connect(out);
    for (const s of parts.sources) s.stop(end + 0.02);
    const first = parts.sources[0];
    if (first) first.onended = () => { for (const n of parts.nodes) n.disconnect(); };
    return { start: t, end, cut: (at) => cut(amp.gain, parts.sources, at) };
  }

  private source(v: VoiceSpec, t: number, parts: Parts): Source {
    const ctx = this.ctx;
    if (v.wave === 'noise') {
      const b = ctx.createBufferSource();
      b.buffer = this.noise;
      b.loop = true;
      // Each noise voice reads from somewhere else in the buffer, so two never play in unison.
      this.noiseOffset = (this.noiseOffset + 0.618034) % 1;
      b.start(t, this.noiseOffset * this.noise.duration);
      parts.nodes.push(b);
      parts.sources.push(b);
      return { out: b, pitch: b.playbackRate, detune: b.detune, scale: 1 / this.noiseHz };
    }
    const osc = ctx.createOscillator();
    osc.start(t);
    parts.nodes.push(osc);
    parts.sources.push(osc);
    const plain = { out: osc, pitch: osc.frequency, detune: osc.detune, scale: 1 };
    if (v.wave !== 'pulse') {
      osc.type = v.wave;
      return plain;
    }
    const width = Math.min(0.95, Math.max(0.05, v.width ?? 0.5));
    if (!v.pwm) {
      osc.setPeriodicWave(this.pulseWave(width));
      return plain;
    }
    // PWM: saw(t) - saw(t - width / f) is a pulse of that width; swinging the delay swings the width.
    osc.type = 'sawtooth';
    const depth = Math.max(0, Math.min(v.pwm.depth, Math.min(width, 1 - width) - 0.02));
    const delay = ctx.createDelay(0.1);
    delay.delayTime.value = width / v.freq;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = v.pwm.rate;
    const swing = ctx.createGain();
    swing.gain.value = depth / v.freq;
    const invert = ctx.createGain();
    invert.gain.value = -1;
    const sum = ctx.createGain();
    sum.gain.value = 0.6;
    lfo.connect(swing).connect(delay.delayTime);
    osc.connect(sum);
    osc.connect(delay).connect(invert).connect(sum);
    lfo.start(t);
    parts.nodes.push(delay, lfo, swing, invert, sum);
    parts.sources.push(lfo);
    return { ...plain, out: sum };
  }

  private pulseWave(width: number): PeriodicWave {
    const key = Math.round(width * WIDTH_STEPS);
    let wave = this.pulses.get(key);
    if (!wave) {
      const { real, imag } = pulseCoefficients(key / WIDTH_STEPS, PULSE_HARMONICS);
      wave = this.ctx.createPeriodicWave(real, imag);
      this.pulses.set(key, wave);
    }
    return wave;
  }

  /** Fixed, gliding, or stepping at the frame rate (the SID arpeggio). */
  private pitch(src: Source, v: VoiceSpec, t: number, gate: number, end: number): void {
    const p = src.pitch;
    const k = src.scale;
    const steps = v.steps;
    if (steps && steps.length > 0) {
      const dt = v.stepTime ?? FRAME;
      const count = v.once ? steps.length : Math.min(MAX_STEPS, Math.ceil((end - t) / dt));
      for (let i = 0; i < count; i++) p.setValueAtTime((steps[i % steps.length] ?? v.freq) * k, t + i * dt);
      return;
    }
    p.setValueAtTime(v.freq * k, t);
    if (v.to !== undefined) p.exponentialRampToValueAtTime(v.to * k, t + (v.glide ?? gate));
  }

  private vibrato(src: Source, vib: NonNullable<VoiceSpec['vibrato']>, t: number, parts: Parts): void {
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = vib.rate;
    const depth = this.ctx.createGain();
    depth.gain.setValueAtTime(0, t);
    depth.gain.setValueAtTime(0, t + vib.delay);
    depth.gain.linearRampToValueAtTime(vib.depth, t + vib.delay + 0.15);
    lfo.connect(depth).connect(src.detune);
    lfo.start(t);
    parts.nodes.push(lfo, depth);
    parts.sources.push(lfo);
  }

  private filter(input: AudioNode, f: FilterSpec, t: number, span: number, parts: Parts): AudioNode {
    const bq = this.ctx.createBiquadFilter();
    bq.type = f.type;
    bq.Q.value = f.q;
    bq.frequency.setValueAtTime(f.freq, t);
    if (f.to !== undefined) bq.frequency.exponentialRampToValueAtTime(f.to, t + (f.sweep ?? span));
    parts.nodes.push(bq);
    return input.connect(bq);
  }

  /**
   * Attack rises in a line; decay and release fall exponentially, as the SID's did. A gate
   * shorter than the attack cuts the attack short rather than overlapping the release.
   */
  private envelope(g: AudioParam, v: VoiceSpec, t: number, gate: number, peak: number): void {
    const e = v.env;
    const attack = Math.min(Math.max(e.a, 0.001), gate);
    g.setValueAtTime(0, t);
    g.linearRampToValueAtTime(peak, t + attack);
    if (gate > attack) g.setTargetAtTime(peak * e.s, t + attack, Math.max(e.d, 0.002) / 4);
    g.setTargetAtTime(0, t + gate, Math.max(e.r, 0.002) / 5);
  }
}

/** Steal a voice: a fast fade from wherever it is, then stop everything it made. */
function cut(g: AudioParam, sources: readonly AudioScheduledSourceNode[], at: number): void {
  if (typeof g.cancelAndHoldAtTime === 'function') g.cancelAndHoldAtTime(at);
  else g.cancelScheduledValues(at);
  g.setTargetAtTime(0, at, 0.008);
  for (const s of sources) {
    try {
      s.stop(at + 0.04);
    } catch {
      // Already stopped: nothing to steal.
    }
  }
}
